import { ToastService } from "@mindmemobile/mm-shared-global";

/* Shared bits for the grid Export buttons (payments, donations, donors, fundraiser tabs). The file
   itself is produced by the API; this side only names it and hands the Blob to the browser. PDF is
   not offered: the API has no layout engine for it and it was agreed to drop it for now. */
export type ExportFormat = "csv" | "xlsx";
/** Kept for the Payments grid, which was written before the export was shared. */
export type PaymentExportFormat = ExportFormat;

/* "<prefix>-YYYYMMDD.<ext>", the same shape the API puts in its Content-Disposition, so the file
   is named identically whichever side names it. */
export const exportFileName = (prefix: string, format: ExportFormat): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  return `${prefix}-${stamp}.${format}`;
};

export const paymentExportFileName = (format: ExportFormat): string =>
  exportFileName("payments", format);

/* How long the object URL and its anchor are kept alive after the click. Safari and Firefox abort a
   download whose object URL is revoked, or whose anchor is removed, while the transfer is still
   starting - which looks exactly like a click that did nothing. The browser gives no signal when
   the transfer has begun, so the cleanup is simply deferred well past it. Same delay FileSaver.js
   arrived at from the same reports. A few MB held for that long costs nothing here. */
const CLEANUP_DELAY_MS = 40000;

type LegacyBlobSaver = (blob: Blob, defaultName?: string) => boolean;
type LegacyNavigator = Navigator & {
  msSaveOrOpenBlob?: LegacyBlobSaver;
  msSaveBlob?: LegacyBlobSaver;
};

/* IE11 and legacy (EdgeHTML) Edge cannot save an object URL at all; these are the only APIs that
   write a Blob to disk there, and no modern browser defines either. Returns false everywhere else
   so the normal path runs. */
const saveViaLegacyBrowser = (blob: Blob, fileName: string): boolean => {
  const legacyNavigator = navigator as LegacyNavigator;
  const save = legacyNavigator.msSaveOrOpenBlob || legacyNavigator.msSaveBlob;
  if (typeof save !== "function") return false;
  save.call(legacyNavigator, blob, fileName);
  return true;
};

/* Safari gained the download attribute in 13. Without it an anchor click navigates instead of
   saving, so those browsers need the new-tab path below. */
const supportsDownloadAttribute = (): boolean =>
  "download" in document.createElement("a");

/* Saves a file through a temporary anchor rather than file-saver, which is only a hoisted root
   dependency and not one this package declares - relying on it would break the moment hoisting
   changed. The steps below are the ones that actually differ between browsers; everything else is
   the same anchor click in all of them. Note that no browser reports whether a download was
   accepted or blocked, so this cannot confirm success: Chrome and Edge refuse repeat downloads
   silently until the site is allowed under Automatic downloads. */
export const downloadBlob = (blob: Blob, fileName: string) => {
  if (saveViaLegacyBrowser(blob, fileName)) return;

  const objectUrl = URL.createObjectURL(blob);
  const revokeLater = () =>
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), CLEANUP_DELAY_MS);

  if (!supportsDownloadAttribute()) {
    /* Opening a tab is the only option left: navigating this window instead would replace the app
       itself, since it runs inside the parent site's iframe. A blocked popup is the one failure
       here that can be detected, so it is reported rather than left silent. */
    if (!window.open(objectUrl, "_blank")) {
      ToastService.showError(
        "Your browser blocked the download. Allow pop-ups for this site and try again."
      );
    }
    revokeLater();
    return;
  }

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  /* Appended because Firefox only acts on a click for an anchor that is in the document, and left
     unstyled on purpose: an anchor with no content is already invisible and takes no space, whereas
     display:none has a history of Safari skipping the click on it. */
  document.body.appendChild(anchor);
  anchor.click();

  window.setTimeout(() => {
    if (anchor.parentNode) anchor.parentNode.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  }, CLEANUP_DELAY_MS);
};

/* The export endpoints answer with a file on success and a JSON error envelope on failure. Because
   the request asks for a Blob, a failure arrives as a Blob of JSON rather than a parsed object, so
   the real message has to be read back out - otherwise every failure looks identical and there is
   nothing to go on when it works for one account and not another. */
const readBlobText = (blob: Blob, done: (text: string) => void) => {
  // FileReader rather than Blob.text(), which Safari only gained in 14.
  const reader = new FileReader();
  reader.onload = () => done(typeof reader.result === "string" ? reader.result : "");
  reader.onerror = () => done("");
  reader.readAsText(blob);
};

const messageFromErrorPayload = (text: string): string => {
  try {
    const parsed = JSON.parse(text);
    const firstError = parsed?.errors?.[0];
    const message = firstError?.message || firstError?.errorMessage || parsed?.message;
    return typeof message === "string" ? message : "";
  } catch {
    return "";
  }
};

/** Shows why an export failed, using the server's own message when it sent one. */
export const showExportError = (error: any, fallback: string) => {
  const payload = error?.response?.data;
  if (payload instanceof Blob) {
    readBlobText(payload, (text) => {
      ToastService.showError(messageFromErrorPayload(text) || fallback);
    });
    return;
  }
  ToastService.showError(fallback);
};

/* An export that comes back as JSON is an error the server sent with a success status, and an empty
   body is a file that never got built. Saving either produces a file that only looks like data, so
   both are treated as failures. */
export const isUsableExportFile = (blob: Blob | undefined): boolean =>
  !!blob && blob.size > 0 && !(blob.type || "").toLowerCase().includes("json");
